import type {
  ProviderAuthenticationCheckpointDetails,
  ProviderAuthenticationIntentResponse,
  PublicAccount,
} from '@unipile/types';
import {
  Button,
  Input,
  LoadingCheckpoint,
  LoginWizardContainer,
  useStartProviderAuthIntent,
  useSolveCheckpoint,
  type WizardComponent,
  useShowLocationConfig,
  LocationConfig,
  Spinner,
  ScopesConsentScreen,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useScopesConsent,
  useHostedAuthSession,
  isRequestCancelled,
  CancelButton,
} from '@unipile/ui';
import { QrCodeIcon, RefreshCcw } from 'lucide-react';
import qrcode from 'qrcode';
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

type AuthMethod = 'qr' | 'pairing';

export const Wizard: WizardComponent = ({ onSuccess, onError, onCancel }) => {
  const { t } = useTranslation();

  const { showLocationConfig } = useShowLocationConfig('whatsapp');

  const { data: session } = useHostedAuthSession();
  const allowMethods = session?.config?.whatsapp?.allow_methods || ['qr', 'pairing'];

  // Scopes consent management - must be shown BEFORE QR code (scanning = immediate connection)
  const { scopes, hasScopes, hasConsented, acceptConsent, brandName } = useScopesConsent({
    provider: 'whatsapp',
  });

  // Start on the first allowed method: fall back to pairing when the app restricts the wizard to it.
  const [method, setMethod] = useState<AuthMethod>(() => (allowMethods.includes('qr') ? 'qr' : 'pairing'));
  const [showQrCode, setShowQrCode] = useState(!showLocationConfig);
  const [loadingCheckpoint, setLoadingCheckpoint] = useState<ProviderAuthenticationCheckpointDetails>();
  const handleLoadingSuccess = (response: ProviderAuthenticationIntentResponse) => {
    if (response.object === 'Account') onSuccess(response);
    if (response.object === 'AuthenticationCheckpoint' && response.checkpoint.type === 'LOADING') {
      setLoadingCheckpoint(response);
    }
  };

  // Show consent screen BEFORE anything else if there are scopes
  if (hasScopes && !hasConsented) {
    return (
      <LoginWizardContainer>
        <ScopesConsentScreen scopes={scopes} brandName={brandName} onAccept={acceptConsent} onCancel={onCancel} />
      </LoginWizardContainer>
    );
  }

  if (loadingCheckpoint) {
    return (
      <LoadingCheckpoint
        intent_id={loadingCheckpoint.intent_id}
        title={t('LOADING.title', { ns: 'whatsapp' })}
        description={t(method === 'pairing' ? 'LOADING.description_pairing' : 'LOADING.description', { ns: 'whatsapp' })}
        onSuccess={handleLoadingSuccess}
        onError={(error) => {
          setLoadingCheckpoint(undefined);
          onError(error);
        }}
      />
    );
  }

  return (
    <LoginWizardContainer>
      <Tabs
        value={method}
        onValueChange={(value) => {
          if (value === 'qr' || value === 'pairing') setMethod(value);
        }}
      >
        {allowMethods.length > 1 && (
          <TabsList className="w-full">
            <TabsTrigger type="button" className="w-full" value="qr">
              {t('Wizard.method.qr', { ns: 'whatsapp' })}
            </TabsTrigger>
            <TabsTrigger type="button" className="w-full" value="pairing">
              {t('Wizard.method.pairing', { ns: 'whatsapp' })}
            </TabsTrigger>
          </TabsList>
        )}
        <TabsContent value="qr">
          <ol className="my-6 ml-6 list-decimal [&>li]:mt-2">
            <li>{t('Wizard.step1', { ns: 'whatsapp' })}</li>
            <li>{t('Wizard.step2', { ns: 'whatsapp' })}</li>
            <li>{t('Wizard.step3', { ns: 'whatsapp' })}</li>
            <li>{t('Wizard.step4', { ns: 'whatsapp' })}</li>
          </ol>
          {showQrCode && <QrCode onSuccess={onSuccess} onError={onError} onLoading={setLoadingCheckpoint} />}
          {!showQrCode && (
            <div className="flex flex-col gap-4">
              <Button onClick={() => setShowQrCode(true)} className="w-full" variant="outline">
                <QrCodeIcon data-icon="inline-start" />
                {t('Wizard.getQrCode', { ns: 'whatsapp' })}
              </Button>
              <LocationConfig provider="whatsapp" />
            </div>
          )}
        </TabsContent>
        <TabsContent value="pairing">
          <ol className="my-6 ml-6 list-decimal [&>li]:mt-2">
            <li>{t('Pairing.step1', { ns: 'whatsapp' })}</li>
            <li>{t('Pairing.step2', { ns: 'whatsapp' })}</li>
            <li>{t('Pairing.step3', { ns: 'whatsapp' })}</li>
            <li>{t('Pairing.step4', { ns: 'whatsapp' })}</li>
          </ol>
          <PairingCode onSuccess={onSuccess} onError={onError} onLoading={setLoadingCheckpoint} />
        </TabsContent>
      </Tabs>
      {!hasScopes && <CancelButton onCancel={onCancel} />}
    </LoginWizardContainer>
  );
};

function QrCode({
  onSuccess,
  onError,
  onLoading,
}: {
  onSuccess: (response: PublicAccount) => void;
  onError: (error: unknown) => void;
  onLoading: (checkpoint: ProviderAuthenticationCheckpointDetails) => void;
}) {
  const { t } = useTranslation();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const authenticationTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const getSignal = useCallback(() => abortControllerRef.current?.signal, []);

  const handleSuccess = (response: ProviderAuthenticationIntentResponse) => {
    if (!abortControllerRef.current) return;

    if (response.object === 'AuthenticationQrCode') {
      if (!canvasRef.current) return;
      qrcode.toCanvas(canvasRef.current, response.qrcode);
      solve.mutate({ data: { code: '', intent_id: response.intent_id } });
    }
    if (response.object === 'AuthenticationCheckpoint' && response.checkpoint.type === 'LOADING') onLoading(response);
    if (response.object === 'Account') onSuccess(response);
  };

  const authenticate = useStartProviderAuthIntent({
    mutationConfig: {
      onSuccess: handleSuccess,
      onError: (error) => {
        if (isRequestCancelled(error)) return;
        onError(error);
      },
    },
    getSignal,
  });
  const solve = useSolveCheckpoint({
    mutationConfig: {
      onSuccess: handleSuccess,
      // Switching auth method aborts the in-flight long poll on unmount; ignore that cancellation.
      onError: (error) => {
        if (isRequestCancelled(error)) return;
        onError(error);
      },
    },
    getSignal,
  });

  const requestQrCode = () => {
    if (authenticationTimeoutRef.current) clearTimeout(authenticationTimeoutRef.current);
    // Why use setTimeout ? Answer : https://github.com/TanStack/query/issues/5341
    authenticationTimeoutRef.current = setTimeout(
      () => authenticate.mutate({ data: { provider: 'whatsapp', credentials: { qrcode: true } } }),
      0,
    );
  };

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    requestQrCode();
    return () => {
      if (authenticationTimeoutRef.current) clearTimeout(authenticationTimeoutRef.current);
      controller.abort();
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative mx-auto h-[276px] w-[276px]">
      {authenticate.isPending && <Spinner />}
      {solve.isError && (
        <div className="absolute flex h-full w-full items-center justify-center rounded bg-white/95">
          <Button onClick={requestQrCode}>
            <RefreshCcw data-icon="inline-start" />
            {t('Wizard.clickToReload', { ns: 'whatsapp' })}
          </Button>
        </div>
      )}

      <canvas ref={canvasRef} width={276} height={276}></canvas>
    </div>
  );
}

function PairingCode({
  onSuccess,
  onError,
  onLoading,
}: {
  onSuccess: (response: PublicAccount) => void;
  onError: (error: unknown) => void;
  onLoading: (checkpoint: ProviderAuthenticationCheckpointDetails) => void;
}) {
  const { t } = useTranslation();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [submittedNumber, setSubmittedNumber] = useState<string>();
  const [code, setCode] = useState<string>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const authenticationTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const getSignal = useCallback(() => abortControllerRef.current?.signal, []);

  const handleSuccess = (response: ProviderAuthenticationIntentResponse) => {
    if (!abortControllerRef.current) return;

    if (response.object === 'AuthenticationCheckpoint' && response.checkpoint.type === 'PAIRING_CODE') {
      setCode(response.checkpoint.code);
      solve.mutate({ data: { code: '', intent_id: response.intent_id } });
    }
    if (response.object === 'AuthenticationCheckpoint' && response.checkpoint.type === 'LOADING') onLoading(response);
    if (response.object === 'Account') onSuccess(response);
  };

  const authenticate = useStartProviderAuthIntent({
    mutationConfig: {
      onSuccess: handleSuccess,
      onError: (error) => {
        if (isRequestCancelled(error)) return;
        onError(error);
      },
    },
    getSignal,
  });
  const solve = useSolveCheckpoint({
    mutationConfig: {
      onSuccess: handleSuccess,
      // Switching auth method aborts the in-flight long poll on unmount; ignore that cancellation.
      onError: (error) => {
        if (isRequestCancelled(error)) return;
        onError(error);
      },
    },
    getSignal,
  });

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    return () => {
      if (authenticationTimeoutRef.current) clearTimeout(authenticationTimeoutRef.current);
      controller.abort();
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    };
  }, []);

  const requestCode = (number: string) => {
    setCode(undefined);
    // Why use setTimeout ? Answer : https://github.com/TanStack/query/issues/5341
    authenticationTimeoutRef.current = setTimeout(
      () => authenticate.mutate({ data: { provider: 'whatsapp', credentials: { phone_number: number } } }),
      0,
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    // WhatsApp expects an international number with digits only (no `+`, spaces or separators).
    const sanitized = phoneNumber.replace(/[^0-9]/g, '');
    if (!sanitized) return;
    setSubmittedNumber(sanitized);
    requestCode(sanitized);
  };

  if (!submittedNumber) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phoneNumber}
          onChange={(event) => setPhoneNumber(event.target.value)}
          placeholder={t('Pairing.phonePlaceholder', { ns: 'whatsapp' })}
        />
        <Button type="submit" className="w-full" disabled={phoneNumber.trim().length === 0}>
          {t('Pairing.getCode', { ns: 'whatsapp' })}
        </Button>
        <LocationConfig provider="whatsapp" />
      </form>
    );
  }

  return (
    <div className="flex min-h-[160px] flex-col items-center justify-center gap-4 text-center">
      {authenticate.isPending && <Spinner />}
      {code && (
        <>
          <p className="text-muted-foreground text-sm">{t('Pairing.enterCode', { ns: 'whatsapp' })}</p>
          <div className="text-3xl font-semibold tracking-[0.3em]">{code}</div>
        </>
      )}
      {solve.isError && (
        <>
          <p className="text-destructive text-sm">{t('Pairing.timeout', { ns: 'whatsapp' })}</p>
          <Button onClick={() => requestCode(submittedNumber)}>
            <RefreshCcw data-icon="inline-start" />
            {t('Pairing.reload', { ns: 'whatsapp' })}
          </Button>
        </>
      )}
    </div>
  );
}
